import config from 'config'

import debug from 'util/debug'
import storage from 'util/storage'
import uuid from 'util/uuid'
import { throttle } from 'util/functions'
import store from 'store'
import { selectAccountPreferenceChatEnabled } from 'selectors/app/selectAccountPreferences'

// Matrix includes
import MatrixClientPeg from 'matrix-react-sdk/lib/MatrixClientPeg'
import * as Lifecycle from 'matrix-react-sdk/lib/Lifecycle'
import Presence from 'matrix-react-sdk/lib/Presence'
import SettingsStore from 'matrix-react-sdk/lib/settings/SettingsStore'

import { doUpdatePresence, doUpdatePresenceSettings } from './actions/presence'
import GroovePresence from './Presence'
import './IndexedDb.worker'
import { BULK_USER_PRESENCE_CHANGED } from './actionTypes/presence'
import { MATRIX_CLIENT_READY } from './actionTypes/client'

const {
  chatServerUrl: MATRIX_CHAT_SERVER_URL,
  chatIdentityServerUrl: MATRIX_IDENTITY_SERVER_URL,
} = config
let presenceEvents = []

class MatrixEnvironment {
  constructor(storeInstance) {
    this.store = storeInstance
    this.loadingPromise = null
    this.loadMatrix()
  }

  isChatEnabled() {
    const { getState } = this.store
    return selectAccountPreferenceChatEnabled(getState())
  }

  async loadMatrix() {
    if (!this.isChatEnabled()) return null
    if (this.loadingPromise) return this.loadingPromise
    const { dispatch } = this.store

    this.loadingPromise = new Promise(async resolve => {
      MatrixClientPeg.setIndexedDbWorkerScript('/chat-sw.js')
      MatrixClientPeg.setCreateOptions({
        storeName: `groove-chat`,
        // For some reason the matrix-js-sdk prefixes the normal index db store
        // with matrix-js-sdk but not the crypto store. Adding the prefix here
        // to keep it consistent
        cryptoStoreName: `matrix-js-sdk:groove-chat-crypto`,
      })

      // Setup Load end to end encryption library
      Presence.setPresenceProvider(GroovePresence)
      await this.setupLoadedSession()
      await this.loadPresenceFromServer()
      await this.subscribe()
      await this.waitForFirstSync()
      // Setup the default matrix configuration settings
      this.presence = Presence.get()
      this.presence.initClient(MatrixClientPeg.get(), this.onAccountPresence)
      Object.freeze(this)
      resolve(this)
      dispatch({ type: MATRIX_CLIENT_READY })
    })

    return this.loadingPromise
  }

  async setupLoadedSession() {
    let loadedSession = await Lifecycle.loadSession({
      fragmentQueryParams: {},
      enableGuest: false,
      guestHsUrl: MATRIX_CHAT_SERVER_URL,
      guestIsUrl: MATRIX_IDENTITY_SERVER_URL,
      defaultDeviceDisplayName: 'None',
    })
    if (!loadedSession) {
      const { chatId: userId, chatAccessToken: accessToken } =
        storage.get('currentUser') || {}
      if (userId && accessToken) {
        const credentials = {
          homeserverUrl: MATRIX_CHAT_SERVER_URL,
          identityServerUrl: MATRIX_IDENTITY_SERVER_URL,
          userId,
          accessToken,
          deviceId: uuid(),
        }
        MatrixClientPeg.replaceUsingCreds(credentials)
        await Lifecycle.doSetLoggedIn(credentials, false)
        loadedSession = true
      }
    }
    if (!loadedSession) {
      throw new Error(
        'CRITICAL: Unable to setup a loaded session. Loading for token and registration failed'
      )
    }
    return true
  }

  onAccountPresence = event => {
    const {
      unavailableTimeMs: autoAway,
      offlineTimeMs: autoOffline,
      state,
      autoPresence,
    } = event.getContent()
    const { dispatch } = this.store
    dispatch(doUpdatePresenceSettings({ autoAway, autoOffline, autoPresence }))
    dispatch(doUpdatePresence(state))
  }

  async subscribe() {
    MatrixClientPeg.get().on('event', event => {
      if (event.getType() === 'm.presence') {
        this.schedulePresenceUpdate(event)
      }
    })
  }

  async loadPresenceFromServer() {
    const serverPresence = await MatrixClientPeg.get().getAccountDataFromServer(
      'g.presence'
    )
    const { state = 'online' } = serverPresence || {}
    MatrixClientPeg.updateSyncPresence(state)
  }

  schedulePresenceUpdate(event) {
    presenceEvents.push(event)

    // This function is debounced to only execute after 500ms. This
    // allows gives the buffer time for us to acumolate presence events
    // before syncing them to state
    this.updatePresenceInState()
  }

  updatePresenceInState = throttle(
    () => {
      this.store.dispatch({
        type: BULK_USER_PRESENCE_CHANGED,
        payload: presenceEvents.reduce((memo, evt) => {
          const userId = evt.getSender()
          const presence = evt.getContent().presence
          if (userId && presence) {
            // eslint-disable-next-line no-param-reassign
            memo.push({ userId, presence })
          }
          return memo
        }, []),
      })
      presenceEvents = []
    },
    500,
    { leading: false }
  )

  waitForFirstSync() {
    if (!this.firstSyncPromise) {
      this.firstSyncPromise = new Promise(resolve => {
        const client = MatrixClientPeg.get()
        client.on('sync', function onSync(state) {
          debug('MatrixClient sync state => %s', state)
          if (state === 'PREPARED') {
            resolve()
            client.off('sync', onSync)
          }
        })
      })
    }
    return this.firstSyncPromise
  }

  getSettingsStore() {
    return SettingsStore
  }

  isNotificationsEnabled() {
    const settings = this.getSettingsStore()
    return (
      settings.getValue('notificationsEnabled') &&
      settings.getValue('audioNotificationsEnabled')
    )
  }

  setNotifications(rules, enabled) {
    const cli = MatrixClientPeg.get()
    const deferreds = rules.map(({ id, kind, actions }) => {
      if (actions) {
        // The new state corresponds to enabling the rule and setting specific actions
        return cli.setPushRuleActions('global', kind, id, actions).then(() => {
          // Then, if requested, enabled or disabled the rule
          if (undefined !== enabled) {
            return cli.setPushRuleEnabled('global', kind, id, enabled)
          }
          return Promise.resolve()
        })
      }
      // The new state corresponds to disabling the rule.
      return cli.setPushRuleEnabled('global', kind, id, enabled)
    })
    return Promise.all(deferreds)
  }

  enableNotifications() {
    const notificationRules = [
      {
        id: '.m.rule.contains_display_name',
        kind: 'override',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight' },
        ],
      },
      {
        id: '.m.rule.contains_user_name',
        kind: 'content',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight' },
        ],
      },
      {
        id: '.m.rule.roomnotif',
        kind: 'override',
        actions: ['notify', { set_tweak: 'highlight' }],
      },
      {
        id: '.m.rule.room_one_to_one',
        kind: 'underride',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight', value: false },
        ],
      },
      {
        id: '.m.rule.encrypted_room_one_to_one',
        kind: 'underride',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight', value: false },
        ],
      },
      {
        id: '.m.rule.message',
        kind: 'underride',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight', value: false },
        ],
      },
      {
        id: '.m.rule.encrypted',
        kind: 'underride',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight', value: false },
        ],
      },
      {
        id: '.m.rule.invite_for_me',
        kind: 'override',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight', value: false },
        ],
      },
      {
        id: '.m.rule.call',
        kind: 'underride',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'ring' },
          { set_tweak: 'highlight', value: false },
        ],
      },
      {
        id: '.m.rule.suppress_notices',
        kind: 'override',
        actions: [
          'notify',
          { set_tweak: 'sound', value: 'default' },
          { set_tweak: 'highlight', value: false },
        ],
      },
      {
        id: '.m.rule.tombstone',
        kind: 'override',
        actions: ['notify', { set_tweak: 'highlight' }],
      },
    ]

    return SettingsStore.setValue('notificationsEnabled', null, 'device', true)
      .then(() =>
        SettingsStore.setValue(
          'audioNotificationsEnabled',
          null,
          'device',
          true
        )
      )
      .then(this.setNotifications(notificationRules, true))
  }

  disableNotifications() {
    const notificationRules = [
      { id: '.m.rule.contains_display_name', kind: 'override', actions: [] },
      { id: '.m.rule.contains_user_name', kind: 'content', actions: [] },
      { id: '.m.rule.roomnotif', kind: 'override', actions: [] },
      {
        id: '.m.rule.room_one_to_one',
        kind: 'underride',
        actions: ['dont_notify'],
      },
      {
        id: '.m.rule.encrypted_room_one_to_one',
        kind: 'underride',
        actions: ['dont_notify'],
      },
      { id: '.m.rule.message', kind: 'underride', actions: ['dont_notify'] },
      { id: '.m.rule.encrypted', kind: 'underride', actions: ['dont_notify'] },
      { id: '.m.rule.invite_for_me', kind: 'override', actions: [] },
      { id: '.m.rule.call', kind: 'underride', actions: [] },
      {
        id: '.m.rule.suppress_notices',
        kind: 'override',
        actions: ['dont_notify'],
      },
      { id: '.m.rule.tombstone', kind: 'override', actions: [] },
    ]
    return SettingsStore.setValue(
      'audioNotificationsEnabled',
      null,
      'device',
      false
    ).then(this.setNotifications(notificationRules, false))
  }

  async get() {
    await this.loadMatrix()
    return MatrixClientPeg.get()
  }
}
export default new MatrixEnvironment(store)
